import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type {
  V2GetLeadsResponse,
  V2GetLeadsDto,
  V2ConvertLeadsDto,
  V2ConvertLeadsResponse,
  V2ReviveLeadsDto,
  V2UpdateLeadsDto,
  V2UpdateLeadsParams,
  V2CheckLeadDto,
  V2GetLeadsByUserIdDto,
  V2CreateLeadDto,
  V2CreateLeadResponse,
} from "./api.dto";

export const useGetLeads = (_data: V2GetLeadsDto) => {
  const { api } = useApi({ version: "v2" });

  const data = {
    ..._data,
    ids: _data.ids && _data.ids?.length > 1 ? _data.ids : _data.ids?.[0],
  };
  return useQuery({
    queryKey: ["leads", data],
    queryFn: async () => {
      const response = await api.get<V2GetLeadsResponse>("/leads", {
        params: filterObjectKeys(data),
      });

      return response.data;
    },
  });
};

export const useGetLeadByUserId = (data: V2GetLeadsByUserIdDto) => {
  const { api } = useApi({ version: "v2" });

  const params = filterObjectKeys(
    {
      ...data,
      ...(data.refIds && data.refIds.length > 0
        ? { refIds: data.refIds, userIds: null }
        : { refIds: null, userIds: data.userId }),
      sort: data.sort ? data.sort : "createdAt",
      order: data.order ? data.order : "desc",
    },
    ["userId"],
  );

  return useQuery({
    queryKey: ["lead", data],
    queryFn: async () => {
      const lead = await api.get<V2GetLeadsResponse>("/leads", {
        params,
      });

      return lead.data;
    },
  });
};

export const useSendLeadInfo = () => {
  const { api } = useApi({ version: "v2" });

  return useMutation({
    mutationKey: ["sendLeadInfo"],
    mutationFn: async (leadId: string) => {
      const response = await api.post<{ message: "accepted" }>(
        `/leads/${leadId}/dispatch`,
      );

      return response.data.message;
    },
  });
};

export const useConvertLeads = () => {
  const { api } = useApi({ version: "v2" });

  return useMutation({
    mutationKey: ["converLeads"],
    mutationFn: async (data: V2ConvertLeadsDto) => {
      const response = await api.post<V2ConvertLeadsResponse>(
        "/leads/convert",
        data,
      );

      return response.data;
    },
  });
};

export const useReviveLeads = () => {
  const { api } = useApi({ version: "v2" });

  return useMutation({
    mutationKey: ["reviveLeads"],
    mutationFn: async (data: V2ReviveLeadsDto) => {
      const response = await api.post<{ message: string }>(
        "/leads/revive",
        data,
      );

      return response.data;
    },
  });
};

export const useUpdateLeads = () => {
  const { api } = useApi({ version: "v2" });
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateLeads"],
    mutationFn: async ({
      data,
      params,
    }: {
      data?: V2UpdateLeadsDto;
      params?: V2UpdateLeadsParams;
    }) => {
      const response = await api.patch<{ message: string }>("/leads", data, {
        params,
      });

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["leads"] });
    },
  });
};

export const useCheckLead = (data: V2CheckLeadDto) => {
  const { api } = useApi({ version: "v2" });

  return useQuery({
    queryKey: ["lead", data],
    queryFn: async () => {
      const response = await api.head("/leads", { params: data });

      return response.status === 200;
    },
    enabled: !!data.telegramId,
  });
};

export const useLeadShares = (leadId: string | undefined, enabled: boolean) => {
  const { api } = useApi({ version: "v2" });

  return useQuery({
    queryKey: ["leadShares", leadId],
    queryFn: async () => {
      const response = await api.get<V2GetLeadsResponse>("/leads", {
        params: {
          ids: leadId,
        },
      });

      return response.data.data[0].shares;
    },
    enabled: enabled && !!leadId,
  });
};

export const useIrrelevantLeadsCount = (userId?: string) => {
  const { api } = useApi({ version: "v2" });

  const { data } = useQuery({
    queryKey: ["irrelevantLeadsCount"],
    queryFn: async () => {
      const response = await api.get<V2GetLeadsResponse>("/leads", {
        params: filterObjectKeys({
          userIds: userId,
          pageSize: 1,
          currentPage: 1,
        }),
      });

      return response.data.total;
    },
  });

  return data;
};

export const useCreateLead = () => {
  const { api } = useApi({ version: "v2" });

  return useMutation({
    mutationKey: ["createLead"],
    mutationFn: async (data: V2CreateLeadDto) =>
      await api.post<V2CreateLeadResponse>("/leads", data),
  });
};
